import { customElement } from 'lit/decorators.js';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataProvider, ProviderBackup } from '../../../webmodule-common/other/data-provider';
import { DataTracker } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events';
import { fileIsImageFile } from '../../../webmodule-common/other/ui/file-helper-functions';
import { fireQuickSuccessToast, fireToastError } from '../../../webmodule-common/other/toast-away';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { Franchisee } from '../../api/dealer-api-interface-franchisee';
import { FranchiseeApi } from '../../api/franchisee-api';
import { getApiFactory } from '../../api/api-injector';
import { getAssetCdnPath } from '../../../webmodule-common/other/common/assets';
import { getToolTip } from '../../../webmodule-common/other/tooltip';
import { html } from 'lit';
import { isAutoSaving } from '../../../webmodule-common/other/save-workflow';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { isSomething } from '../../../webmodule-common/other/general/nothing';
import { LangFranchiseeBrandingView } from '../../../localization/scripts/franchisee/view/franchisee-branding-view-lang';
import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { tlang } from '../../../webmodule-common/other/language/lang';
import { uploadVirtualFile, VirtualFile } from '../data/image-upload';
import { userSecurity } from '../../../webmodule-common/other/api/user-security';

@customElement('wm-franchiseebrandingview')
export class FranchiseeBrandingView extends PageControlTabWithIndependantSaving {
  private franchiseeApi: FranchiseeApi = getApiFactory().franchisee();
  private franchiseeBackup: ProviderBackup<Franchisee>;
  private dataBinding: DataBinding;

  private dataTracker: DataTracker;
  private positiveLogoName = 'positive-logo';
  private negativeLogoName = 'negative-logo';
  private newPositiveLogo?: File;
  private newNegativeLogo?: File;

  constructor(franchiseeDataProvider: DataProvider<Franchisee>) {
    super();
    this.pageFragment = 'branding';
    this.franchiseeBackup = new ProviderBackup<Franchisee>(franchiseeDataProvider);

    this.dataBinding = new DataBinding(this.ui, this.elementId, input => {
      return `${input}-${this.elementId}`;
    });
    this.dataTracker = new DataTracker(this.dataBinding);
    this.dataTracker.addImageLink(
      this.positiveLogoName,
      () => this.getAbsoluteVirtualFilePath(this.franchisee.positiveLogoVirtualPath),
      value => {
        this.newPositiveLogo = value;
      }
    );

    this.dataTracker.addImageLink(
      this.negativeLogoName,
      () => this.getAbsoluteVirtualFilePath(this.franchisee.negativeLogoVirtualPath),
      value => {
        this.newNegativeLogo = value;
      }
    );
  }

  protected async revertChanges(): Promise<boolean> {
    this.newNegativeLogo = undefined;
    this.newNegativeLogo = undefined;

    return Promise.resolve(true);
  }

  get isAdmin() {
    return userSecurity().isAdmin() || userSecurity().isSupplier();
  }

  public isDataReadonly(): boolean {
    return !this.isAdmin;
  }

  public allowDeletePage(): boolean {
    return false;
  }

  protected getCaption(): Snippet {
    return LangFranchiseeBrandingView.branding();
  }

  getValidationErrors(): string[] {
    const errors: string[] = [];
    // throw image upload error only when a file has been provided, but it isn't a valid image
    if (this.newPositiveLogo && !fileIsImageFile(this.newPositiveLogo)) {
      errors.push(LangFranchiseeBrandingView.provideValidColorLogo());
    }
    // throw image upload error only when a file has been provided, but it isn't a valid image
    if (this.newNegativeLogo && !fileIsImageFile(this.newNegativeLogo)) {
      errors.push(LangFranchiseeBrandingView.provideValidWhiteLogo());
    }
    return errors;
  }

  async prepareForSave(): Promise<void> {
    this.dataTracker.applyChangeToValue();
  }

  public internalDataChanged(): boolean {
    return isSomething(this.newNegativeLogo, this.newPositiveLogo);
  }

  async onEnter(): Promise<void> {
    this.franchiseeBackup.reset();
    this.requestUpdate();
  }

  get franchisee(): Franchisee {
    return this.franchiseeBackup.item;
  }

  protected async internalSaveData(): Promise<boolean> {
    // attempt to upload a logo from the given element and get its uploaded file path

    const newPositiveLogo = await this.dataBinding.createVirtualFile(this.newPositiveLogo);
    if (newPositiveLogo) {
      if (await this.uploadLogoFromElement(newPositiveLogo, 'positive'))
        this.franchisee.positiveLogoVirtualPath = newPositiveLogo?.filePath ?? this.franchisee.positiveLogoVirtualPath;
    }
    const newNegativeLogo = await this.dataBinding.createVirtualFile(this.newNegativeLogo);
    if (newNegativeLogo) {
      if (await this.uploadLogoFromElement(newNegativeLogo, 'negative'))
        this.franchisee.negativeLogoVirtualPath = newNegativeLogo?.filePath ?? this.franchisee.negativeLogoVirtualPath;
    }
    const result = await this.franchiseeBackup.commitChanges();
    if (result) {
      if (!isAutoSaving()) fireQuickSuccessToast(LangFranchiseeBrandingView.brandingSaved());
      this.dataTracker.removeFilesFromEditors();
      this.requestUpdate();
      return true;
    }

    return false;
  }

  private async uploadLogoFromElement(fileInformation: VirtualFile, newFileName: string): Promise<boolean> {
    // only attempt an upload if we have information about the image file to be uploaded.
    if (fileInformation) {
      // get the virtual logo path from the API and attempt to upload the file content.
      fileInformation.filePath = this.franchiseeApi.createBrandingLogoImagePath(newFileName, fileInformation.extension);
      const logoResult = await uploadVirtualFile(fileInformation);
      if (logoResult) {
        // if successful remove the local uploaded file to avoid repeated uploads.
        return true;
      }
      fireToastError(new Error(LangFranchiseeBrandingView.unableToUploadLogo()));
    }
    // if we get here no logo image has been uploaded, so return null as the path.
    return false;
  }

  /**
   * Attempts to get the absolute virtual file path for a given relative file path.
   * @param relativeVirtualFilePath The relative virtual file path.
   * @returns The full absolute virtual file path.
   */
  private getAbsoluteVirtualFilePath(relativeVirtualFilePath: string | null | undefined): string {
    if (!isEmptyOrSpace(relativeVirtualFilePath))
      return this.franchiseeApi.api.fullUrl(`api/file/${relativeVirtualFilePath}`);
    else return getAssetCdnPath('images/sample-logo-colour.png');
  }

  protected bodyTemplate(): EventTemplate {
    const form = new FormInputAssistant(this.dataTracker, false, true);

    return html` <div>
      <form id="CompanyBrandingForm" class="form-two-col">
        <webmodule-card class="settings-spec-card settings-spec-card--row">
          <p class="specs-intro">
            ${tlang`Upload your business logo. It is added to documents sent to %%client%%s and %%supplier%%s (%%estimate%%s, %%invoice%%s, %%purchase-order%%s) and appears at the top of printed PDFs and reports.`}
          </p>
          <ul class="specs-list">
            <li>
              <webmodule-icon library="fa" name="fas-expand"></webmodule-icon>
              <div class="specs-text">
                <span class="specs-label">${tlang`Minimum size`}</span>
                <span class="specs-value">800 &times; 400 px</span>
              </div>
            </li>
            <li>
              <webmodule-icon library="fa" name="fas-star"></webmodule-icon>
              <div class="specs-text">
                <span class="specs-label">${tlang`Ideal size`}</span>
                <span class="specs-value">1600 &times; 800 px</span>
              </div>
            </li>
            <li>
              <webmodule-icon library="fa" name="fas-image"></webmodule-icon>
              <div class="specs-text">
                <span class="specs-label">${tlang`Format`}</span>
                <span class="specs-value">JPG &middot; 300 DPI &middot; ${tlang`white background`}</span>
              </div>
            </li>
          </ul>
          <div class="specs-notes">
            <p>
              <webmodule-icon library="fa" name="fas-circle-info"></webmodule-icon>
              <span>${tlang`Logos with a different aspect ratio are scaled down proportionally to fit.`}</span>
            </p>
          </div>
        </webmodule-card>
        <div class="row branding-wrapper">
          <div class="col-sm-6 form-column">
            <webmodule-card class="settings-upload-card">
              <h4 slot="header" class="settings-upload-card-title">
                ${LangFranchiseeBrandingView.colorLogo()}
                ${getToolTip(tlang`This logo appears on the top left hand corner of your masthead.`)}
              </h4>
              ${form.imagePicker(this.positiveLogoName, LangFranchiseeBrandingView.colorLogo())}
            </webmodule-card>
          </div>
          <div class="col-sm-6 form-column">
            <webmodule-card class="settings-upload-card">
              <h4 slot="header" class="settings-upload-card-title">
                ${LangFranchiseeBrandingView.whiteLogo()}
                ${getToolTip(tlang`This logo appears on printed reports (PDF).`)}
              </h4>
              ${form.imagePicker(this.negativeLogoName, LangFranchiseeBrandingView.whiteLogo())}
            </webmodule-card>
          </div>
        </div>
      </form>
    </div>`;
  }
}
